/**
 * EVERY CONNECTOR, IN ONE PLACE.
 *
 * Connectors grew up wherever the work was being done: LeadSquared and HubSpot
 * became settings tabs, WooCommerce and the shipping partners landed under
 * Settings > Store, the ad-platform senders under Signal Studio > Destinations,
 * the checkout apps inside the install code, the calling providers under
 * Calling. Each placement was reasonable on the day. Together they meant the
 * owner of the product could not find a connector he had built, and a client
 * had no way to see what was connected at all.
 *
 * This list is the index. It holds only the facts the Integrations page needs
 * to show a connector and send you to it - never the connector's own form,
 * which stays in the component its settings card already renders (the page
 * mounts that same component in a drawer; see pages/integrations-page).
 *
 * Per entry:
 *   key       matches the key GET /api/integrations/status answers for (the
 *             server's list lives in api/src/services/integrationStatus.js; a
 *             test asserts the two are the same set)
 *   category  one of CATEGORIES
 *   kinds     'store' | 'leadgen' | 'both' - which workspaces it is FOR. The
 *             other kind still sees it behind "Show all", and anything
 *             connected always shows, whatever it is for.
 *   blurb     one line: what it does for the client, not what it is
 *   config    a SECOND place it can be set up, when one exists: { tab, sub? }
 *             (a settings page in lib/settings-tabs, sub = one of its
 *             sections) or { route } (an app route). Absent when this page's
 *             drawer is the only home - every connector Settings used to hold
 *             (LeadSquared, HubSpot, calendars, payments, calling...) and every
 *             card the Destinations page used to hold (retired 29 Sep 2026)
 *             moved here. Tests check the place exists.
 *   docs      knowledgebase path (docs-site/src/content/docs), optional #anchor.
 *             The ONE place a connector's guide path lives: the drawer's
 *             "Setup guide" and Settings' pointers both read it through
 *             lib/guides guideForIntegration().
 *   source    where the status is measured, in words
 *   noun      what one unit of traffic is called ("order", "booking")
 *   dir       'receiving' | 'sending' | 'both' - picks the verb on the chip
 *   panel     the drawer body, resolved by the page. Every connector has
 *             one: a drawer that only linked elsewhere was a dead end.
 *             'store:<platform>', 'checkout:<app>', 'dest:<destinations key>',
 *             'esp:<provider>', 'calling:<provider>', 'ai-calling:<provider>',
 *             or a plain name the page's Panel switch knows.
 *   detected  true when there is nothing to configure: we recognise it from
 *             the data it sends (checkout apps, Cal.com, the Appbrew app)
 *   builtin   true for the product's own rules (lead event routing, the
 *             catalog id format): nothing to connect, nothing to measure.
 *             The card reads "Built in" and stays out of the tallies.
 *   whenConfigured  true for a route that exists only for a workspace that
 *             set it up (the two Google Sheet routes): absent from the page,
 *             the chips and the tallies until its status says it is set.
 *   maturity  'live' | 'beta', stamped from MATURITY below (never written on
 *             an entry by hand): live = proven on real traffic in a client
 *             workspace in the last 30 days AND its contract tests pass;
 *             beta = everything else. A beta card wears a small Beta chip and
 *             its page says why in one line (maturityWhy).
 *
 * Pure data and pure functions - no JSX, no icons - so jest can load it.
 */
import { SETTINGS_TABS, WHATSAPP_SECTIONS, settingsPath } from './settings-tabs';
import { KB_BASE } from './guides';
import { timeAgo } from './display-format';

export const DOCS_BASE = KB_BASE;
export const docsUrl = (path) => (path ? `${DOCS_BASE}${path}` : null);

/**
 * The two halves of the page, in the order they are read. Destinations is
 * where your signals GO (the page Destinations used to be); Sources is
 * everything that brings data IN. Every category belongs to one of them.
 */
export const GROUPS = [
  { key: 'destinations', label: 'Destinations', blurb: 'Where your conversions, events and audiences go.' },
  { key: 'sources', label: 'Sources', blurb: 'What brings data in: store, spend, CRM, calls.' },
];

export const CATEGORIES = [
  { key: 'sending', group: 'destinations', label: 'Ad platforms', blurb: 'Conversions, events and audiences sent to ad platforms.' },
  { key: 'export', group: 'destinations', label: 'Your own systems', blurb: 'Every event to your endpoints, and your warehouse.' },
  { key: 'engagement', group: 'destinations', label: 'Engagement tools', blurb: 'Push, SMS, email and WhatsApp journeys.' },
  { key: 'store', group: 'sources', label: 'Store', blurb: 'Where your orders come from.' },
  { key: 'checkout', group: 'sources', label: 'Shopify checkout apps', blurb: '1-click checkouts on Shopify; nothing to connect.' },
  { key: 'shipping', group: 'sources', label: 'Shipping', blurb: 'Delivered, NDR and RTO after an order ships.' },
  { key: 'payments', group: 'sources', label: 'Payments', blurb: 'Payments, subscriptions and payment links.' },
  { key: 'spend', group: 'sources', label: 'Ad spend', blurb: 'What you spent, read against revenue.' },
  { key: 'crm', group: 'sources', label: 'CRM', blurb: 'Your sales CRM, in step with leads here.' },
  { key: 'booking', group: 'sources', label: 'Booking and calendar', blurb: 'Meetings booked, on your team’s calendars.' },
  { key: 'leadsources', group: 'sources', label: 'Lead sources', blurb: 'Leads from ad platforms’ own forms.' },
  { key: 'email', group: 'sources', label: 'Email', blurb: 'Email platforms and sending providers.' },
  { key: 'messaging', group: 'sources', label: 'WhatsApp and messaging', blurb: 'WhatsApp and verification codes.' },
  { key: 'calling', group: 'sources', label: 'Calling', blurb: 'Click-to-call for reps, an AI caller for leads.' },
  { key: 'mobile', group: 'sources', label: 'Mobile app', blurb: 'Your store’s app, measured like your website.' },
  { key: 'data', group: 'sources', label: 'Data and automation', blurb: 'Other systems pushing data in.' },
];

/** The group an entry sits in: its category's. */
export const groupOf = (entry) => CATEGORIES.find((c) => c.key === entry.category)?.group || 'sources';

// The order a workspace reads the page in. A store opens on where its orders
// come from; a lead business on where its leads go and how meetings get
// booked. Categories missing from a list are appended in CATEGORIES order.
export const CATEGORY_ORDER = {
  store: ['sending', 'export', 'engagement', 'store', 'checkout', 'shipping', 'payments', 'spend', 'mobile', 'messaging', 'email', 'calling', 'data', 'crm', 'booking', 'leadsources'],
  leadgen: ['sending', 'export', 'engagement', 'crm', 'booking', 'leadsources', 'messaging', 'email', 'calling', 'spend', 'payments', 'data', 'store', 'checkout', 'shipping', 'mobile'],
};

const GUIDE = '/getting-started/integrations/';

export const INTEGRATIONS = [
  // ── Store ────────────────────────────────────────────────────────────────
  { key: 'shopify', name: 'Shopify', category: 'store', kinds: 'store',
    blurb: 'Orders by webhook, joined to the visit that made them.',
    page: '/integrations/shopify', docs: '/getting-started/connect-shopify/', source: 'orders from Shopify',
    noun: 'order', dir: 'receiving' },
  { key: 'woocommerce', name: 'WooCommerce', category: 'store', kinds: 'store',
    blurb: 'Verified order webhooks, with the visitor on each order.',
    docs: '/getting-started/connect-woocommerce/', source: 'store webhook landing log',
    noun: 'order', dir: 'receiving', panel: 'store:woocommerce' },
  { key: 'bigcommerce', name: 'BigCommerce', category: 'store', kinds: 'store',
    blurb: 'Order webhooks created for you, joined by cart id.',
    docs: '/getting-started/connect-bigcommerce/', source: 'store webhook landing log',
    noun: 'order', dir: 'receiving', panel: 'store:bigcommerce' },
  { key: 'magento', name: 'Magento', category: 'store', kinds: 'store',
    blurb: 'Signed order deliveries from Adobe Commerce, with a pull.',
    docs: '/getting-started/connect-magento/', source: 'store webhook landing log',
    noun: 'order', dir: 'receiving', panel: 'store:magento' },

  // ── Checkout ─────────────────────────────────────────────────────────────
  { key: 'shopflo', name: 'Shopflo', category: 'checkout', kinds: 'store', detected: true,
    blurb: 'Checkout start and purchase, with email and phone.',
    page: '/integrations/shopify?sub=checkout&partner=shopflo', docs: '/getting-started/install-tracking/#checkout-apps-and-wallets',
    source: 'partner checkout feed and pixel events', noun: 'checkout event', dir: 'receiving' },
  { key: 'gokwik', name: 'GoKwik', category: 'checkout', kinds: 'store', detected: true,
    blurb: 'Start, address, payment and purchase, with full identity.',
    page: '/integrations/shopify?sub=checkout&partner=gokwik', docs: '/getting-started/install-tracking/#checkout-apps-and-wallets',
    source: 'partner checkout feed and pixel events', noun: 'checkout event', dir: 'receiving' },
  { key: 'shiprocket_checkout', name: 'Shiprocket Checkout', category: 'checkout', kinds: 'store', detected: true,
    blurb: 'Every checkout step with email, phone and pincode.',
    page: '/integrations/shopify?sub=checkout&partner=shiprocket', docs: '/getting-started/install-tracking/#checkout-apps-and-wallets',
    source: 'partner checkout feed and pixel events', noun: 'checkout event', dir: 'receiving' },
  { key: 'pragma', name: 'Pragma', category: 'checkout', kinds: 'store', detected: true,
    blurb: 'The checkout end to end, with name and address.',
    page: '/integrations/shopify?sub=checkout&partner=pragma', docs: '/getting-started/install-tracking/#checkout-apps-and-wallets',
    source: 'pixel events', noun: 'checkout event', dir: 'receiving' },

  // ── Shipping ─────────────────────────────────────────────────────────────
  { key: 'shiprocket', name: 'Shiprocket', category: 'shipping', kinds: 'store',
    blurb: 'Delivered, NDR and RTO on each order.',
    docs: '/commerce/shipping-partners/', source: 'shipping webhook landing log',
    noun: 'shipment update', dir: 'receiving', panel: 'shipping' },
  { key: 'delhivery', name: 'Delhivery', category: 'shipping', kinds: 'store',
    blurb: 'Tracking pushes from Delhivery, matched by AWB.',
    docs: '/commerce/shipping-partners/', source: 'shipping webhook landing log',
    noun: 'shipment update', dir: 'receiving', panel: 'shipping' },
  { key: 'clickpost', name: 'ClickPost', category: 'shipping', kinds: 'store',
    blurb: 'Every carrier ClickPost tracks, through one webhook.',
    docs: '/commerce/shipping-partners/', source: 'shipping webhook landing log',
    noun: 'shipment update', dir: 'receiving', panel: 'shipping' },
  { key: 'shipping_generic', name: 'Another shipping partner', category: 'shipping', kinds: 'store',
    blurb: 'Any partner that posts a tracking webhook.',
    docs: '/commerce/shipping-partners/', source: 'shipping webhook landing log',
    noun: 'shipment update', dir: 'receiving', panel: 'shipping' },

  // ── Payments ─────────────────────────────────────────────────────────────
  { key: 'stripe', name: 'Stripe', category: 'payments', kinds: 'both',
    blurb: 'Payments and subscriptions as orders and workflow triggers.',
    docs: '/getting-started/connect-stripe/', source: 'payment webhook log',
    noun: 'payment event', dir: 'receiving', panel: 'stripe' },
  { key: 'samcart', name: 'SamCart', category: 'payments', kinds: 'both',
    blurb: 'Orders, refunds and subscription changes from SamCart.',
    docs: '/getting-started/connect-samcart/', source: 'payment webhook log',
    noun: 'payment event', dir: 'receiving', panel: 'samcart' },
  { key: 'razorpay', name: 'Razorpay payment links', category: 'payments', kinds: 'leadgen',
    blurb: 'Payment links on WhatsApp, marked paid when paid.',
    docs: '/getting-started/razorpay-payment-links/', source: 'paid Razorpay links',
    noun: 'paid link', dir: 'receiving', panel: 'razorpay' },

  // ── CRM ──────────────────────────────────────────────────────────────────
  { key: 'leadsquared', name: 'LeadSquared', category: 'crm', kinds: 'leadgen',
    blurb: 'Leads pushed to LeadSquared; stage moves brought back.',
    docs: '/leads/connect-leadsquared/', source: 'CRM sync log',
    noun: 'sync', dir: 'both', panel: 'leadsquared' },
  { key: 'hubspot', name: 'HubSpot', category: 'crm', kinds: 'both',
    blurb: 'Contacts and leads kept in step, both ways.',
    docs: '/leads/hubspot/', source: 'CRM sync log',
    noun: 'sync', dir: 'both', panel: 'hubspot' },
  { key: 'zoho_crm', name: 'Zoho CRM', category: 'crm', kinds: 'leadgen',
    blurb: 'Zoho posts leads and stages; the journey written back.',
    docs: '/leads/zoho-crm/', source: 'CRM post log',
    noun: 'post', dir: 'both', panel: 'zoho' },
  // Any CRM with a webhook (docs/CRM_LEAD_FLOW.md): the mapper learns its
  // shape from the first post; no credentials.
  { key: 'crm_custom', name: 'Another CRM', category: 'crm', kinds: 'leadgen',
    blurb: "Any CRM's webhook posts leads and stage changes.",
    docs: '/leads/own-forms-and-crm/', source: 'CRM post log',
    noun: 'post', dir: 'receiving', panel: 'crm_custom' },

  // ── Booking and calendar ─────────────────────────────────────────────────
  { key: 'calendly', name: 'Calendly', category: 'booking', kinds: 'leadgen',
    blurb: 'A Calendly booking becomes a meeting here.',
    docs: '/meetings/calendly/', source: 'meetings booked through Calendly',
    noun: 'booking', dir: 'receiving', panel: 'calendly' },
  { key: 'calcom', name: 'Cal.com', category: 'booking', kinds: 'leadgen', detected: true,
    blurb: 'Meetings the AI caller books in Cal.com.',
    docs: '/meetings/booking/#other-booking-tools', source: 'meetings booked through Cal.com',
    noun: 'booking', dir: 'receiving', panel: 'calcom' },
  { key: 'google_calendar', name: 'Google Calendar and Meet', category: 'booking', kinds: 'leadgen',
    blurb: 'Each rep’s free time, and a Meet link.',
    docs: '/meetings/booking/#calendar-and-conferencing', source: 'meetings with a Google event',
    noun: 'meeting', dir: 'sending', panel: 'calendar' },
  { key: 'microsoft_calendar', name: 'Microsoft Outlook and Teams', category: 'booking', kinds: 'leadgen',
    blurb: 'Outlook availability, and a Teams link on bookings.',
    docs: '/meetings/booking/#calendar-and-conferencing', source: 'meetings with an Outlook event',
    noun: 'meeting', dir: 'sending', panel: 'calendar' },
  { key: 'zoom', name: 'Zoom', category: 'booking', kinds: 'leadgen',
    blurb: 'A Zoom meeting on every booking.',
    docs: '/meetings/booking/#calendar-and-conferencing', source: 'meetings with a Zoom meeting',
    noun: 'meeting', dir: 'sending', panel: 'calendar' },

  // ── Lead sources ─────────────────────────────────────────────────────────
  { key: 'meta_lead_ads', name: 'Meta Lead Ads', category: 'leadsources', kinds: 'leadgen',
    blurb: 'Instant-form leads, in the pipeline within seconds.',
    docs: '/leads/meta-lead-ads/', source: 'lead ads webhook log',
    noun: 'lead', dir: 'receiving', panel: 'leadads' },
  { key: 'snap_lead_ads', name: 'Snapchat Lead Ads', category: 'leadsources', kinds: 'leadgen',
    blurb: 'Snapchat lead form leads, in the pipeline within seconds.',
    docs: '/leads/snapchat-lead-ads/', source: 'Snapchat lead log',
    noun: 'lead', dir: 'receiving', panel: 'snapleads' },
  { key: 'linkedin_lead_ads', name: 'LinkedIn Lead Gen Forms', category: 'leadsources', kinds: 'leadgen',
    blurb: 'LinkedIn lead gen form leads, in the pipeline within minutes.',
    docs: '/leads/linkedin-lead-gen-forms/', source: 'LinkedIn lead log',
    noun: 'lead', dir: 'receiving', panel: 'linkedinleads' },

  // ── Email ────────────────────────────────────────────────────────────────
  { key: 'klaviyo', name: 'Klaviyo', category: 'email', kinds: 'both',
    blurb: 'Contacts kept in step with Klaviyo, with source.',
    docs: '/email/setup-and-sequences/#esp-sync', source: 'email sync log',
    noun: 'contact sync', dir: 'sending', panel: 'esp:klaviyo' },
  { key: 'kit', name: 'Kit (ConvertKit)', category: 'email', kinds: 'both',
    blurb: 'New leads added to Kit and tagged.',
    docs: '/email/setup-and-sequences/#esp-sync', source: 'email sync log',
    noun: 'contact sync', dir: 'sending', panel: 'esp:kit' },
  { key: 'mailchimp', name: 'Mailchimp', category: 'email', kinds: 'both',
    blurb: 'Contacts added to your Mailchimp audience.',
    docs: '/email/setup-and-sequences/#esp-sync', source: 'email sync log',
    noun: 'contact sync', dir: 'sending', panel: 'esp:mailchimp' },
  { key: 'activecampaign', name: 'ActiveCampaign', category: 'email', kinds: 'both',
    blurb: 'Contacts synced to ActiveCampaign lists.',
    docs: '/email/setup-and-sequences/#esp-sync', source: 'email sync log',
    noun: 'contact sync', dir: 'sending', panel: 'esp:activecampaign' },
  { key: 'email_sending', name: 'Email sending provider', category: 'email', kinds: 'both',
    blurb: 'The provider your workflow and sequence emails leave through.',
    docs: '/email/setup-and-sequences/#sending-setup', source: 'outbound email log',
    noun: 'email', dir: 'sending', panel: 'email' },

  // ── WhatsApp and messaging ───────────────────────────────────────────────
  { key: 'whatsapp_cloud', name: 'WhatsApp Cloud API', category: 'messaging', kinds: 'both',
    blurb: 'Your WhatsApp number: inbox, templates, broadcasts, recovery.',
    // Opens the WhatsApp page, not a drawer: every WhatsApp setting lives there
    // (connection, templates, replies, catalog, widget, cost) - more than a
    // drawer holds, and it was a Settings tab until 26 Sep 2026.
    page: '/integrations/whatsapp', docs: '/whatsapp/setup/', source: 'WhatsApp message log',
    noun: 'message', dir: 'both' },
  { key: 'otp_sms', name: 'SMS for phone verification', category: 'messaging', kinds: 'leadgen',
    blurb: 'MSG91 or Twilio Verify, checking phones before they become leads.',
    docs: '/leads/phone-verification/#set-up-a-channel-first', source: 'verification code log',
    noun: 'code', dir: 'sending', panel: 'verification' },

  // ── Calling ──────────────────────────────────────────────────────────────
  ...[
    ['exotel', 'Exotel'], ['twilio', 'Twilio'], ['knowlarity', 'Knowlarity'], ['myoperator', 'MyOperator'],
    ['mcube', 'MCube'], ['ozonetel', 'Ozonetel'], ['servetel', 'Servetel'],
  ].map(([key, name]) => ({
    key, name, category: 'calling', kinds: 'leadgen',
    blurb: 'Click-to-call from a lead, with the recording logged.',
    docs: '/meetings/calling/#set-up-click-to-call', source: 'call log',
    noun: 'call', dir: 'sending', panel: `calling:${key}`,
  })),
  { key: 'calling_generic', name: 'Another calling provider', category: 'calling', kinds: 'leadgen',
    blurb: 'Any HTTP call API, from a request template.',
    docs: '/meetings/calling/#another-provider', source: 'call log', noun: 'call', dir: 'sending', panel: 'calling:generic' },
  { key: 'vapi', name: 'Vapi (AI calls)', category: 'calling', kinds: 'both',
    blurb: 'An AI voice agent for new leads and abandoned checkouts.',
    docs: '/meetings/calling/#set-up-ai-calls', source: 'AI call log',
    noun: 'AI call', dir: 'sending', panel: 'ai-calling:vapi' },
  { key: 'bolna', name: 'Bolna (AI calls)', category: 'calling', kinds: 'both',
    blurb: 'Indian-language AI calls for qualification and recovery.',
    docs: '/meetings/calling/#set-up-ai-calls', source: 'AI call log',
    noun: 'AI call', dir: 'sending', panel: 'ai-calling:bolna' },
  { key: 'myoperator_ai', name: 'MyOperator (AI calls)', category: 'calling', kinds: 'both',
    blurb: 'AI calls on your MyOperator line, outcome written back.',
    docs: '/meetings/calling/#set-up-ai-calls', source: 'AI call log',
    noun: 'AI call', dir: 'sending', panel: 'ai-calling:myoperator' },
  { key: 'ai_calling_generic', name: 'Another AI calling provider', category: 'calling', kinds: 'both',
    blurb: 'Any AI voice platform with an HTTP API.',
    docs: '/meetings/calling/#set-up-ai-calls', source: 'AI call log',
    noun: 'AI call', dir: 'sending', panel: 'ai-calling:generic' },

  { key: 'call_tracking', name: 'Call tracking', category: 'calling', kinds: 'both',
    blurb: 'Tracked numbers, so every call arrives with its source.',
    docs: '/meetings/call-tracking/', source: 'inbound call log',
    noun: 'inbound call', dir: 'receiving', panel: 'call-tracking' },

  // ── Ad platforms: sending ────────────────────────────────────────────────
  { key: 'meta_capi', name: 'Meta Conversions API', category: 'sending', kinds: 'both',
    blurb: 'Server-side events to Meta, deduplicated against the pixel.',
    docs: '/signals/meta-capi/', source: 'Meta CAPI send log', noun: 'event', dir: 'sending', panel: 'dest:meta' },
  // One Google Ads card. The Ads API connection ("Report a conversion" workflow
  // steps, the conversion-action picker) sits inside it behind a fold; its
  // status key google_ads_api is probed but FOLDED (see integrationStatus).
  { key: 'google_ads_dm', name: 'Google Ads conversions', category: 'sending', kinds: 'both',
    blurb: 'Purchases and lead stages uploaded as Google Ads conversions.',
    docs: '/signals/google/#path-1-google-data-manager-direct', source: 'Google Ads send log',
    noun: 'conversion', dir: 'sending', panel: 'dest:google_dm' },
  { key: 'ga4', name: 'Google Analytics 4', category: 'sending', kinds: 'both',
    blurb: 'Lead and purchase events to GA4, server-side.',
    docs: '/signals/google/#google-analytics-4', source: 'GA4 send log', noun: 'event', dir: 'sending', panel: 'dest:ga4' },
  { key: 'sgtm', name: 'Server-side GTM', category: 'sending', kinds: 'both',
    blurb: 'Your events, posted to a server container you already run.',
    docs: '/signals/server-gtm/', source: 'server GTM send log', noun: 'event', dir: 'sending', panel: 'dest:sgtm' },
  { key: 'sgtm_enrichment', name: 'sGTM enrichment', category: 'sending', kinds: 'both',
    blurb: 'Your server tags ask us who the visitor is.',
    // Lives on the Server GTM page now (owner, 25 Oct 2026); the card and
    // /integrations/sgtm_enrichment open its tab there.
    page: '/server-container?view=enrichment',
    docs: '/signals/server-gtm/#sgtm-enrichment', source: 'enrichment lookups', noun: 'lookup', dir: 'sending', panel: 'dest:enrich' },
  ...[
    ['tiktok', 'TikTok Events API', 'event'], ['linkedin', 'LinkedIn Conversions API', 'conversion'],
    ['snapchat', 'Snapchat Conversions API', 'event'], ['pinterest', 'Pinterest Conversions API', 'event'],
    ['reddit', 'Reddit Conversions API', 'event'], ['openai', 'ChatGPT Ads Conversions API', 'event'],
  ].map(([key, name, noun]) => ({
    key, name, category: 'sending', kinds: 'both',
    blurb: 'Conversions sent server-side with hashed identity and click id.',
    docs: key === 'openai' ? '/signals/other-ad-platforms/#chatgpt-ads' : '/signals/other-ad-platforms/', source: 'conversions API send log',
    noun, dir: 'sending', panel: `dest:${key}`,
  })),
  // Built 12 Oct 2026 (owner: "build 5, 6"). Each has its own section of the
  // guide; nothing is sent until a cell of the events matrix is switched on.
  { key: 'microsoft', name: 'Microsoft Ads Conversions API', category: 'sending', kinds: 'both',
    blurb: 'Server-side conversions to Microsoft Ads, beside your UET tag.',
    docs: '/signals/other-ad-platforms/#microsoft-ads', source: 'conversions API send log', noun: 'event', dir: 'sending', panel: 'dest:microsoft' },
  { key: 'x', name: 'X (Twitter) Conversions API', category: 'sending', kinds: 'both',
    blurb: 'Conversions to X by its click id and hashed identity.',
    docs: '/signals/other-ad-platforms/#x-twitter', source: 'conversions API send log', noun: 'conversion', dir: 'sending', panel: 'dest:x' },
  { key: 'taboola', name: 'Taboola conversions', category: 'sending', kinds: 'both',
    blurb: 'Sales from Taboola ads reported back by their click id.',
    docs: '/signals/other-ad-platforms/#taboola', source: 'conversions API send log', noun: 'event', dir: 'sending', panel: 'dest:taboola' },
  { key: 'outbrain', name: 'Outbrain conversions', category: 'sending', kinds: 'both',
    blurb: 'Sales from Outbrain ads reported back by their click id.',
    docs: '/signals/other-ad-platforms/#outbrain', source: 'conversions API send log', noun: 'event', dir: 'sending', panel: 'dest:outbrain' },
  { key: 'meta_audiences', name: 'Meta custom audiences', category: 'sending', kinds: 'both',
    blurb: 'Your buyers kept in step with Meta audiences.',
    docs: '/signals/audiences/', source: 'last audience sync',
    noun: 'audience sync', dir: 'sending', panel: 'dest:audiences' },
  // Beside the Meta sender, not under WhatsApp: the catalog is what content_ids
  // are checked against, and what WhatsApp product messages draw images from.
  { key: 'meta_catalog', name: 'Meta product catalog', category: 'sending', kinds: 'store',
    blurb: 'Your Meta catalog, for product ids and WhatsApp messages.',
    config: { route: '/integrations/whatsapp?sub=catalog' }, docs: '/whatsapp/setup/#connect-your-product-catalog', source: 'catalog sync',
    noun: 'catalog sync', dir: 'receiving', panel: 'catalog' },
  // The product's own rules, on their own cards as Destinations had them.
  { key: 'catalog_ids', name: 'Catalog product IDs', category: 'sending', kinds: 'store', builtin: true,
    blurb: 'Which product id goes into content_ids, per platform.',
    docs: '/signals/meta-capi/#catalog-product-ids-stores', source: 'saved id format',
    noun: 'format', dir: 'sending', panel: 'dest:catalog_ids' },
  { key: 'routing', name: 'Lead event routing', category: 'sending', kinds: 'leadgen', builtin: true,
    blurb: 'Where a lead’s Qualified and Converted events go.',
    docs: '/signals/signal-loop/#the-event-routing-card', source: 'none kept',
    noun: 'rule', dir: 'sending', panel: 'dest:routing' },

  // ── Ad platforms: spend ──────────────────────────────────────────────────
  { key: 'meta_spend', name: 'Meta Ads spend', category: 'spend', kinds: 'both',
    blurb: 'Spend by campaign, ad set and ad, every few hours.',
    docs: '/signals/meta-capi/#meta-ads-spend', source: 'ad spend table', noun: 'spend sync', dir: 'receiving', panel: 'dest:meta_ads' },
  { key: 'google_spend', name: 'Google Ads spend', category: 'spend', kinds: 'both',
    blurb: 'Google Ads spend and clicks by campaign.',
    docs: '/signals/google/#google-ads-spend', source: 'ad spend table', noun: 'spend sync', dir: 'receiving', panel: 'dest:google_spend' },
  { key: 'spend_sheet', name: 'Google Ads spend by Sheet', category: 'spend', kinds: 'both', whenConfigured: true,
    blurb: 'Google Ads spend from a Sheet your script fills.',
    docs: '/signals/google/#google-ads-spend-by-sheet', source: 'ad spend table', noun: 'spend sync', dir: 'receiving', panel: 'spend-sheet' },

  // ── Mobile app ───────────────────────────────────────────────────────────
  { key: 'appbrew', name: 'Appbrew', category: 'mobile', kinds: 'store', detected: true,
    blurb: 'Your app’s screens, carts and orders, joined to your website.',
    docs: '/tracking/mobile-app-appbrew/', source: 'app events and app orders',
    noun: 'app event', dir: 'receiving', panel: 'dest:mobile_app' },

  // ── Data and automation ──────────────────────────────────────────────────
  { key: 'partner_api', name: 'Partner API', category: 'data', kinds: 'both',
    blurb: 'Your systems push leads, stages and checkouts in.',
    docs: '/leads/lead-collection/#partner-api-and-csv', source: 'partner API request log',
    noun: 'request', dir: 'receiving', panel: 'api' },
  // ── Engagement tools (19 Oct 2026, services/engageSync) ─────────────────
  // The brand's own push, SMS, email and WhatsApp tool: orders, checkouts and
  // each customer's profile, keyed on the id its SDK uses. Nothing is sent
  // until a cell of the events matrix is switched on.
  { key: 'moengage', name: 'MoEngage', category: 'engagement', kinds: 'store',
    blurb: 'Orders, checkouts and customer profiles for your journeys.',
    docs: '/signals/moengage/', source: 'engagement send log', noun: 'event', dir: 'sending', panel: 'engage:moengage' },
  { key: 'webengage', name: 'WebEngage', category: 'engagement', kinds: 'store',
    blurb: 'Orders, checkouts and customer profiles for your journeys.',
    docs: '/signals/webengage/', source: 'engagement send log', noun: 'event', dir: 'sending', panel: 'engage:webengage' },
  { key: 'clevertap', name: 'CleverTap', category: 'engagement', kinds: 'store',
    blurb: 'Orders, checkouts and customer profiles for your journeys.',
    docs: '/signals/clevertap/', source: 'engagement send log', noun: 'event', dir: 'sending', panel: 'engage:clevertap' },
  { key: 'outbound_webhooks', name: 'Outbound webhooks', category: 'export', kinds: 'both',
    blurb: 'Every lead, stage and order event posted to your endpoints.',
    config: { route: '/tracking/webhooks' }, docs: '/account/api-and-webhooks/#outbound-webhooks', source: 'webhook delivery log',
    noun: 'delivery', dir: 'sending', panel: 'webhooks' },
  { key: 'sheets_bridge', name: 'Google Ads conversions by Sheet', category: 'sending', kinds: 'both', whenConfigured: true,
    blurb: 'Conversions written to a Sheet that Google Ads imports.',
    docs: '/signals/google/#path-2-the-sheets-bridge-no-api-access-needed', source: 'none kept',
    noun: 'row', dir: 'sending', panel: 'dest:google_sheet' },
  { key: 'firestore', name: 'Firestore cookie store', category: 'data', kinds: 'both',
    blurb: 'Identity cookies read from your own Firestore.',
    docs: `${GUIDE}#data-and-automation`, source: 'none kept',
    noun: 'lookup', dir: 'receiving', panel: 'dest:firestore' },
  { key: 'server_container', name: 'Server GTM container', category: 'export', kinds: 'both',
    blurb: 'Your Tag Manager server container, hosted on your tracking subdomain.',
    config: { route: '/server-container' }, docs: '/tracking/server-gtm-container/', source: 'requests your container answered',
    page: '/server-container?view=hosted',
    noun: 'request', dir: 'receiving', panel: 'server-container' },
  { key: 'bigquery', name: 'BigQuery export', category: 'export', kinds: 'both',
    blurb: 'Orders, customers, sessions and signals in your BigQuery, nightly.',
    docs: '/account/bigquery-export/', source: 'export log',
    noun: 'daily export', dir: 'sending', panel: 'bigquery' },
];

// ─── Maturity: what the page may honestly call proven ──────────────────────
//
// "What the integrations page shows is what actually works" (roadmap, 19 Oct
// 2026). Measured that day from prod's own logs, read-only: each LIVE entry
// names the evidence, real traffic in a client workspace in the last 30 days
// (send logs, webhook landing logs, sync logs, pixel events), with its
// contract and payload tests passing (api/test/capi_adapters,
// connectors_inbound_real, google_*, audience_sync_backoff). Everything else
// is beta, with one plain line why. Re-measure before moving a key: a
// connector earns live by running, not by being built.
export const MATURITY = {
  live: {
    shopify: 'Orders from 13 stores in 30 days',
    gokwik: 'Checkout feed and pixel events from three stores',
    pragma: 'Checkout events from one store',
    shiprocket_checkout: 'Checkout events from one store',
    samcart: '2,653 payment events from one workspace',
    meta_lead_ads: 'Leads into three workspaces',
    google_calendar: 'Meetings booked in two workspaces',
    email_sending: 'Workflow emails sent from six workspaces',
    whatsapp_cloud: 'Messages from six workspaces',
    bolna: 'AI calls placed in three workspaces',
    meta_capi: 'Events sent for eight workspaces',
    google_ads_dm: 'Conversions uploaded for two stores',
    ga4: 'Events sent for two workspaces',
    meta_audiences: 'Audience synced for one store',
    meta_spend: 'Spend pulled for eight workspaces',
    google_spend: 'Spend pulled for five workspaces',
    server_container: 'Containers answering for three workspaces',
    partner_api: 'Requests from one workspace',
  },
  // One line why, where "not yet run on a client account" is not the reason.
  why: {
    shopflo: 'Not seen on a client store in the last 30 days.',
    sgtm: 'No send to a client container succeeded in the last 30 days.',
    firestore: 'Its lookups are not logged, so it cannot be proven yet.',
    appbrew: 'The app package has not sent events from a client app yet.',
    otp_sms: 'Codes went by WhatsApp; no SMS provider has sent one yet.',
  },
  defaultWhy: 'Not yet run on a client account.',
};
for (const e of INTEGRATIONS) {
  if (e.builtin) continue;
  e.maturity = MATURITY.live[e.key] ? 'live' : 'beta';
  e.maturityWhy = e.maturity === 'beta' ? (MATURITY.why[e.key] || MATURITY.defaultWhy) : null;
}
export const isBeta = (entry) => entry?.maturity === 'beta';

/**
 * Tools we only RECOGNISE - in a visit's source, an order's user agent or its
 * note attributes - and never connect to. Listing them as connectors would
 * promise a set-up screen that does not exist; leaving them out leaves the
 * client wondering why their WhatsApp tool is "missing". So they get a section
 * of their own that says exactly what we do with them.
 * (Sources: services/commerce/channel.js OWNED_TOOL, and services/adPlatform.js
 * APP_NAMES read by commerce/journey.js appSurfaceOf.)
 */
export const LOOKALIKES = [
  { key: 'bitespeed', name: 'Bitespeed', what: 'WhatsApp marketing' },
  { key: 'wati', name: 'WATI', what: 'WhatsApp marketing' },
  { key: 'interakt', name: 'Interakt', what: 'WhatsApp marketing' },
  { key: 'aisensy', name: 'AiSensy', what: 'WhatsApp marketing' },
  { key: 'postscript', name: 'Postscript', what: 'SMS marketing' },
  { key: 'tapcart', name: 'Tapcart', what: 'Mobile app builder' },
  { key: 'vajro', name: 'Vajro', what: 'Mobile app builder' },
  { key: 'plobal', name: 'Plobal', what: 'Mobile app builder' },
  { key: 'shopney', name: 'Shopney', what: 'Mobile app builder' },
  { key: 'apptile', name: 'Apptile', what: 'Mobile app builder' },
  { key: 'mobiloud', name: 'MobiLoud', what: 'Mobile app builder' },
  { key: 'twinr', name: 'Twinr', what: 'Mobile app builder' },
  { key: 'swipecart', name: 'Swipecart', what: 'Mobile app builder' },
  { key: 'appmaker', name: 'Appmaker', what: 'Mobile app builder' },
];

// ─── Where it lives today ──────────────────────────────────────────────────

// null = this page's drawer is its only home.
export function configHref(entry) {
  const c = entry?.config || {};
  if (c.route) return c.route;
  if (c.tab) return settingsPath(c.tab, c.sub);
  return null;
}

export function configLabel(entry) {
  const c = entry?.config || {};
  if (c.tab) {
    const t = SETTINGS_TABS.find((x) => x.value === c.tab);
    const sec = c.sub && t?.sections?.find((x) => x.value === c.sub);
    return `Settings > ${t ? t.label : c.tab}${sec ? ` > ${sec.label}` : ''}`;
  }
  if (c.route && c.route.startsWith('/integrations/whatsapp')) {
    const sub = new URLSearchParams(c.route.split('?')[1] || '').get('sub');
    const sec = WHATSAPP_SECTIONS.find((x) => x.value === sub);
    return `Messaging > WA Setup${sec ? ` > ${sec.label}` : ''}`;
  }
  if (c.route === '/tracking/webhooks') return 'Measurement > Webhooks';
  if (c.route === '/server-container') return 'Measurement > Server GTM container';
  return c.route || null;
}

// ─── The retired Destinations page ─────────────────────────────────────────

/**
 * /tracking/destinations?d=<card> was the address of every sender's card until
 * 29 Sep 2026. Those links live on in bookmarks, in emails the product sent,
 * in guides and in the two Google consent round trips, so each one lands on
 * the same card here: the connector whose drawer mounts it (panel
 * 'dest:<card>'), or the Signal catalogue tab a card had already moved to.
 *
 * Pure: takes the old query string (with or without the "?") and says where
 * to go. `ecomOn` decides Audiences, which a store keeps under the catalogue
 * and a lead business under Integrations - the same split the old page made.
 */
export const DESTINATION_MOVED = { ecom_events: '/studio?tab=events' };
export function integrationForDestination(card) {
  return INTEGRATIONS.find((e) => e.panel === `dest:${card}`)?.key || null;
}
export function fromDestinationsLink(search, { ecomOn = false } = {}) {
  const q = new URLSearchParams(String(search || '').replace(/^\?/, ''));
  const card = q.get('d');
  q.delete('d');
  // The one-shot OAuth results ride along; the page reads them once and
  // opens the card they concern.
  if (q.get('gdm')) q.set('open', 'google_ads_dm');
  let path = '/integrations';
  if (card === 'audiences' && ecomOn) path = '/studio?tab=audiences';
  else if (DESTINATION_MOVED[card]) path = DESTINATION_MOVED[card];
  else if (card) {
    const key = integrationForDestination(card);
    if (key) q.set('open', key);
    else q.set('cat', 'destinations');
  } else if (!q.get('open')) q.set('cat', 'destinations');
  const [p, own = ''] = path.split('?');
  for (const [k, v] of new URLSearchParams(own)) q.set(k, v);
  const qs = q.toString();
  return `${p}${qs ? `?${qs}` : ''}`;
}

// ─── Ordering for a workspace kind ─────────────────────────────────────────

/** Is this entry for this kind of workspace? */
export const forKind = (entry, kind) => entry.kinds === 'both' || entry.kinds === kind;

/**
 * Does this entry exist for this workspace at all? A `whenConfigured` route
 * (the Google Sheet routes) is on the page only once its status says it is
 * set up - connected, or set up and switched off. Until then it is absent:
 * not hidden behind "Show all", not counted, not a chip.
 */
export const exists = (entry, st) => !entry.whenConfigured || !!st?.connected || (!!st?.setup && st.setup !== 'none');

/**
 * Categories in the order this workspace reads them: Destinations' categories
 * first, then Sources', each group in its own reading order for the kind.
 */
export function orderedCategories(kind, group = null) {
  const order = CATEGORY_ORDER[kind] || CATEGORY_ORDER.leadgen;
  const rest = CATEGORIES.map((c) => c.key).filter((k) => !order.includes(k));
  const all = [...order, ...rest].map((k) => CATEGORIES.find((c) => c.key === k)).filter(Boolean);
  const byGroup = GROUPS.flatMap((g) => all.filter((c) => c.group === g.key));
  return group ? byGroup.filter((c) => c.group === group) : byGroup;
}

// ─── Status in words ───────────────────────────────────────────────────────

const MIN = 60 * 1000;
const HOUR = 60 * MIN;
const DAY = 24 * HOUR;

/** "4 min ago", "3 hours ago", "2 days ago". Never "0 min". */
// The product's one wording for how long ago (lib/display-format timeAgo).
export const agoWords = (ts, now = Date.now()) => timeAgo(ts, { now });

const VERB = { receiving: 'Receiving', sending: 'Sending', both: 'Working' };
const plural = (n) => (/[^aeiou]y$/.test(n) ? `${n.slice(0, -1)}ies` : `${n}s`);

/**
 * The chip on a card: tone + one plain sentence. `st` is the connector's entry
 * from GET /api/integrations/status; missing (still loading, or the page is
 * older than the server) reads as checking rather than as "Not connected".
 */
export function statusLine(entry, st, now = Date.now()) {
  // The product's own rules: nothing to connect, so nothing to check.
  if (entry.builtin) return { tone: 'info', text: 'Built in, always on' };
  if (!st) return { tone: 'calm', text: 'Checking…' };
  // The Destinations badge, folded in. A destination that is set up but
  // switched off, or linked with nothing mapped, is neither "not connected"
  // nor working, and the pill names the step - the same reading the
  // Destinations page makes from the card's own config.
  if (st.setup === 'off') return { tone: 'warn', text: 'Configured, switched off' };
  if (st.setup === 'unmapped') return { tone: 'warn', text: 'Connected, no events mapped' };
  const noun = entry.noun || 'event';
  const ago = agoWords(st.last_event_at, now);
  switch (st.state) {
    case 'active':
      return { tone: 'good', text: `${VERB[entry.dir] || 'Working'}, last ${noun} ${ago}` };
    case 'silent': {
      // No timestamp means nothing inside the window the probe reads (a month
      // or more), not necessarily "ever" - so "recently", not "yet".
      if (!st.last_event_at) return { tone: 'warn', text: `Connected, no ${plural(noun)} recently` };
      const days = Math.floor((now - new Date(st.last_event_at).getTime()) / DAY);
      return { tone: 'warn', text: days >= 1 ? `Connected, silent for ${days} ${days === 1 ? 'day' : 'days'}` : `Connected, last ${noun} ${ago}` };
    }
    case 'needs_attention': {
      // A named error is dated (lib/failure-words rule): "Last send failed 2
      // days ago: ...", never a bare message that reads as current for ever.
      const what = entry.dir === 'receiving' ? 'delivery' : entry.dir === 'both' ? noun : 'send';
      const when = agoWords(st.last_error_at, now);
      if (st.last_error) return { tone: 'bad', text: `Last ${what} failed${when ? ` ${when}` : ''}: ${st.last_error}` };
      return { tone: 'bad', text: `${st.error_count} failed in 7 days` };
    }
    case 'not_connected':
      return { tone: 'calm', text: entry.detected ? 'Not seen on your store yet' : 'Not connected' };
    default:
      // 'unknown': we could not tell. Say why; a card that cannot compute its
      // status is not allowed to look like it did.
      return { tone: 'info', text: st.connected ? `Connected. ${st.reason || 'We cannot measure its traffic.'}` : (st.reason || 'We could not check it.') };
  }
}

/** Counts for the four tiles at the top of the page. */
export function tally(entries, statuses = {}) {
  const t = { connected: 0, active: 0, silent: 0, attention: 0, unknown: 0 };
  for (const e of entries) {
    const s = statuses[e.key];
    if (!s || e.builtin) continue;
    if (s.connected) t.connected += 1;
    if (s.state === 'active') t.active += 1;
    else if (s.state === 'silent') t.silent += 1;
    else if (s.state === 'needs_attention') t.attention += 1;
    else if (s.state === 'unknown') t.unknown += 1;
  }
  return t;
}
